import { useMemo, useState, type JSX, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  Inline,
  Stack,
  StatePill,
  type DataTableColumn,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import { formatTimestampSafe } from "../../../locale-format.js";

import { ApiError, apiGet, apiSend } from "../../../lib/api.js";
import { GtccCapHint } from "../../GtccCapHint.js";

/**
 * A-GTCC-SI1 — Support-Inbox im Cockpit (Register Zeile 40).
 *
 * Vorlage ist die gtcc1-Seite `apps/admin/src/support/SupportInboxPage.tsx`.
 * Gelesen wird derselbe, bereits vorhandene Plattform-Owner-Endpunkt
 * `GET /v1/admin/support-tickets` (`services/api/src/modules/support/routes.ts:144`).
 * Einen Detail-Endpunkt gibt es nicht — die Liste liefert das vollstaendige
 * Ticket, das Detail ist also eine Auswahl auf den bereits geladenen Daten.
 *
 * A-GTCC-40-WRITE1 — die eine Schreibaktion der alten Seite ist nachgezogen:
 * `POST /v1/admin/support-tickets/:id/close`
 * (`services/api/src/modules/support/routes.ts:179`). Es ist derselbe,
 * bereits vorhandene Endpunkt mit demselben Guard
 * (`requirePlatformOwner("support-tickets")`, `routes.ts:192`) — kein neues
 * Recht, keine Migration. Anders als die alte Seite
 * (`apps/admin/src/support/SupportInboxPage.tsx:183-192`, Klick schliesst
 * sofort) verlangt das Cockpit nach dem Muster von #2165 einen zweiten,
 * eigenen Bestaetigungsklick.
 *
 * Bewusst NICHT enthalten: Antworten — dafuer gibt es keinen Endpunkt.
 */

type SupportTicketStatus = "open" | "closed";

interface SupportTicket {
  readonly id: string;
  readonly tenantId: string;
  readonly reporterSub: string;
  readonly reporterEmail: string | null;
  readonly category: string;
  readonly subject: string;
  readonly description: string;
  readonly pagePath: string | null;
  readonly userAgent: string | null;
  readonly status: SupportTicketStatus;
  readonly closedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface SupportListResponse {
  readonly items: readonly SupportTicket[];
  readonly nextCursor: string | null;
}

const TICKET_LIMIT = 100;
// `module_request` is written by the module-management route, not by the
// general support form. It remains a normal support ticket in the inbox so
// operators can filter it alongside the established categories.
const CATEGORIES = [
  "bug",
  "feature_request",
  "account",
  "billing",
  "module_request",
  "other",
] as const;

type StatusFilter = "" | SupportTicketStatus;

function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

function statusTone(status: SupportTicketStatus): StatePillTone {
  return status === "closed" ? "success" : "warn";
}

/** Alter eines Tickets als relative Zeit — Intl, keine neue Dependency. */
function formatAge(createdAt: string, formatter: Intl.RelativeTimeFormat, now: number): string {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return "—";
  const minutes = Math.round((created - now) / 60_000);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

export function GtccSupportInboxView(): JSX.Element {
  const { t, locale } = useLocale();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }),
    [locale],
  );
  const relativeFormatter = useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    [locale],
  );

  const ticketsQuery = useQuery<SupportListResponse, Error>({
    queryKey: ["gtcc", "support-tickets", statusFilter === "" ? "all" : statusFilter],
    queryFn: ({ signal }) =>
      apiGet<SupportListResponse>(
        "/v1/admin/support-tickets",
        { limit: TICKET_LIMIT, ...(statusFilter === "" ? {} : { status: statusFilter }) },
        signal,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const tickets = useMemo(() => ticketsQuery.data?.items ?? [], [ticketsQuery.data?.items]);
  const visible = useMemo(
    () =>
      categoryFilter === "" ? tickets : tickets.filter((it) => it.category === categoryFilter),
    [tickets, categoryFilter],
  );
  const counts = useMemo(
    () => ({
      open: tickets.filter((it) => it.status === "open").length,
      closed: tickets.filter((it) => it.status === "closed").length,
    }),
    [tickets],
  );
  const selected = useMemo(
    () => visible.find((it) => it.id === selectedId) ?? null,
    [visible, selectedId],
  );
  const now = Date.now();

  const columns: readonly DataTableColumn<SupportTicket>[] = [
    {
      id: "status",
      header: t("admin.gtcc.support.col.status"),
      cell: (ticket) => (
        <StatePill
          tone={statusTone(ticket.status)}
          label={t(`admin.gtcc.support.status.${ticket.status}`)}
        />
      ),
    },
    {
      id: "tenant",
      header: t("admin.gtcc.support.col.tenant"),
      cell: (ticket) => <code>{ticket.tenantId}</code>,
    },
    {
      id: "subject",
      header: t("admin.gtcc.support.col.subject"),
      cell: (ticket) => (
        <Stack gap={0}>
          <span>{ticket.subject}</span>
          <span
            style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-xs)" }}
          >
            {t(`support.category.${ticket.category}`)}
          </span>
        </Stack>
      ),
    },
    {
      id: "age",
      header: t("admin.gtcc.support.col.age"),
      cell: (ticket) => (
        <time
          dateTime={ticket.createdAt}
          title={formatTimestampSafe(ticket.createdAt, (d) => dateFormatter.format(d))}
        >
          {formatAge(ticket.createdAt, relativeFormatter, now)}
        </time>
      ),
    },
    {
      id: "detail",
      header: t("admin.gtcc.support.col.detail"),
      align: "end",
      cell: (ticket) => (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setSelectedId((current) => (current === ticket.id ? null : ticket.id))}
          data-testid={`gtcc-support-detail-toggle-${ticket.id}`}
        >
          {selectedId === ticket.id
            ? t("admin.gtcc.support.action.hide")
            : t("admin.gtcc.support.action.show")}
        </Button>
      ),
    },
    {
      id: "actions",
      header: t("admin.gtcc.support.write.actions"),
      align: "end",
      cell: (ticket) => (ticket.status === "open" ? <CloseTicketAction ticket={ticket} /> : null),
    },
  ];

  const tableError = ticketsQuery.isError
    ? {
        title: isForbidden(ticketsQuery.error)
          ? t("admin.gtcc.support.forbidden")
          : t("admin.gtcc.support.error"),
      }
    : null;

  return (
    <Stack gap={4} data-testid="gtcc-view-sup" style={{ padding: "var(--gt-sys-space-5)" }}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.support.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.support.subtitle")}
        </p>
        <Inline gap={2} wrap>
          <StatePill tone="info" label={t("admin.gtcc.support.write.enabled")} />
        </Inline>
      </Stack>

      <Card elevation={1}>
        <Stack gap={3}>
          <Inline gap={2} wrap data-testid="gtcc-support-summary">
            <StatePill
              tone="neutral"
              label={`${t("admin.gtcc.support.summary.loaded")}: ${tickets.length}`}
            />
            <StatePill
              tone="warn"
              label={`${t("admin.gtcc.support.summary.open")}: ${counts.open}`}
            />
            <StatePill
              tone="success"
              label={`${t("admin.gtcc.support.summary.closed")}: ${counts.closed}`}
            />
          </Inline>

          <Inline gap={3} wrap align="end" data-testid="gtcc-support-filters">
            <label style={FILTER_LABEL_STYLE}>
              {t("admin.gtcc.support.filter.status")}
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.currentTarget.value as StatusFilter);
                  setSelectedId(null);
                }}
                data-testid="gtcc-support-filter-status"
                style={SELECT_STYLE}
              >
                <option value="">{t("admin.gtcc.support.filter.all")}</option>
                <option value="open">{t("admin.gtcc.support.status.open")}</option>
                <option value="closed">{t("admin.gtcc.support.status.closed")}</option>
              </select>
            </label>
            <label style={FILTER_LABEL_STYLE}>
              {t("admin.gtcc.support.filter.category")}
              <select
                value={categoryFilter}
                onChange={(event) => {
                  setCategoryFilter(event.currentTarget.value);
                  setSelectedId(null);
                }}
                data-testid="gtcc-support-filter-category"
                style={SELECT_STYLE}
              >
                <option value="">{t("admin.gtcc.support.filter.all")}</option>
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`support.category.${category}`)}
                  </option>
                ))}
              </select>
            </label>
          </Inline>

          <DataTable
            caption={t("admin.gtcc.support.table_caption")}
            columns={columns}
            rows={visible}
            getRowId={(ticket) => ticket.id}
            loading={ticketsQuery.isPending}
            loadingLabel={t("admin.gtcc.support.loading")}
            error={tableError}
            empty={{ title: t("admin.gtcc.support.empty") }}
            testId="gtcc-support-table"
            rowTestId={(ticket) => `gtcc-support-row-${ticket.id}`}
          />
          {ticketsQuery.isSuccess && ticketsQuery.data ? (
            <GtccCapHint
              limit={TICKET_LIMIT}
              shown={tickets.length}
              cursor={ticketsQuery.data.nextCursor}
              testId="gtcc-support-cap"
            />
          ) : null}
        </Stack>
      </Card>

      <Card elevation={1}>
        <Stack gap={2} data-testid="gtcc-support-detail">
          <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
            {t("admin.gtcc.support.detail.title")}
          </h3>
          {selected === null ? (
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.support.detail.none")}
            </p>
          ) : (
            <Stack gap={2} data-testid={`gtcc-support-detail-${selected.id}`}>
              <Inline gap={2} wrap>
                <StatePill
                  tone={statusTone(selected.status)}
                  label={t(`admin.gtcc.support.status.${selected.status}`)}
                />
                <span style={{ fontWeight: 500 }}>{selected.subject}</span>
              </Inline>
              <DetailField label={t("admin.gtcc.support.detail.reporter")}>
                {selected.reporterEmail ?? selected.reporterSub}
              </DetailField>
              <DetailField label={t("admin.gtcc.support.detail.tenant")}>
                <code>{selected.tenantId}</code>
              </DetailField>
              <DetailField label={t("admin.gtcc.support.detail.category")}>
                {t(`support.category.${selected.category}`)}
              </DetailField>
              <DetailField label={t("admin.gtcc.support.detail.created")}>
                {formatTimestampSafe(selected.createdAt, (d) => dateFormatter.format(d))}
              </DetailField>
              {selected.closedAt === null ? null : (
                <DetailField label={t("admin.gtcc.support.detail.closed_at")}>
                  {formatTimestampSafe(selected.closedAt, (d) => dateFormatter.format(d))}
                </DetailField>
              )}
              {selected.pagePath === null ? null : (
                <DetailField label={t("admin.gtcc.support.detail.page_path")}>
                  <code>{selected.pagePath}</code>
                </DetailField>
              )}
              {selected.userAgent === null ? null : (
                <DetailField label={t("admin.gtcc.support.detail.user_agent")}>
                  <span style={{ wordBreak: "break-word" }}>{selected.userAgent}</span>
                </DetailField>
              )}
              <DetailField label={t("admin.gtcc.support.detail.description")}>
                <pre style={DESCRIPTION_STYLE}>{selected.description}</pre>
              </DetailField>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * Schliessen eines Tickets, zweistufig: der erste Klick deckt die
 * Bestaetigung auf, erst der zweite schickt den POST. Ein Fehler wird
 * als `role="alert"` neben der Zeile gemeldet, nicht verschluckt.
 */
function CloseTicketAction({ ticket }: { readonly ticket: SupportTicket }): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useMutation<unknown, Error, void>({
    mutationFn: () => apiSend("POST", `/v1/admin/support-tickets/${ticket.id}/close`),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "support-tickets"] });
    },
    onError: () => setError(t("admin.gtcc.support.write.error")),
  });

  return (
    <Inline gap={2} wrap align="center">
      <Button
        type="button"
        variant="ghost"
        disabled={close.isPending}
        onClick={() => {
          setError(null);
          setConfirming((current) => !current);
        }}
        data-testid={`gtcc-support-close-${ticket.id}`}
      >
        {t("admin.gtcc.support.write.close")}
      </Button>
      {confirming ? (
        <Button
          type="button"
          variant="primary"
          disabled={close.isPending}
          onClick={() => {
            setError(null);
            close.mutate();
          }}
          data-testid={`gtcc-support-close-confirm-${ticket.id}`}
        >
          {t("admin.gtcc.support.write.confirm")}
        </Button>
      ) : null}
      {error === null ? null : <span role="alert">{error}</span>}
    </Inline>
  );
}

function DetailField({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <Stack gap={0}>
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-xs)",
          color: "var(--gt-sys-text-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span>{children}</span>
    </Stack>
  );
}

const FILTER_LABEL_STYLE = {
  display: "grid",
  gap: 4,
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
} as const;

const SELECT_STYLE = {
  fontSize: "var(--gt-sys-font-size-sm)",
  paddingBlock: 6,
  paddingInline: 10,
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
} as const;

const DESCRIPTION_STYLE = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  fontFamily: "inherit",
} as const;
